import { useCustomer, useInvoice } from '@melio/ar-domain';
import { useToast } from '@melio/penny';
import { useBoolean } from '@melio/platform-utils';
import { useMemo, useState } from 'react';

import { OnBeforeDone } from '../../../types';
import { CreateInvoiceFormFields, SubmitTarget } from '../types';
import { useSuccessMessage } from './useSuccessMessage';

type Props = {
  invoiceId: string;
  onError?: ErrorFunction;
  onDone?: (id: string) => unknown;
  onBeforeDone?: OnBeforeDone;
};
export const useIssueInvoiceActivity = ({ invoiceId, onBeforeDone, ...props }: Props) => {
  const onError = (error: PlatformError) => {
    props.onError?.(error);
    toastFactory({ type: 'error', title: error.message });
  };

  const [submitTarget, setSubmitTarget] = useState<SubmitTarget>();
  const toastFactory = useToast();
  const getSuccessMessage = useSuccessMessage();

  const { paymentRequestLink, shareInvoice, issueInvoice, customer, isLoading, isSaving, isDraft } = useInvoiceActions({
    invoiceId,
    onError,
  });

  const [isOnBeforeDone, onBeforeDoneState] = useBoolean(false);
  const submitHandler = (formData: CreateInvoiceFormFields, target?: SubmitTarget) => {
    if (onBeforeDone) {
      onBeforeDoneState.on();
      onBeforeDone(() => handleShareInvoice(formData, target), onBeforeDoneState.off);
    } else {
      void handleShareInvoice(formData, target);
    }
  };

  const handleShareInvoice = async (formData: CreateInvoiceFormFields, target?: SubmitTarget) => {
    setSubmitTarget(target);
    try {
      let link = paymentRequestLink ?? '';
      if (isDraft) {
        const data = await issueInvoice(formData);
        if (data.paymentRequest?.link) link = data.paymentRequest.link;
      }

      if (formData.shouldSendEmail === 'yes') {
        if (isDraft) {
          await shareInvoice({ sentFrom: 'partner', type: 'createInvoice' });
        } else {
          await shareInvoice({ sentFrom: 'partner', type: 'updateInvoice' });
        }
      } else {
        await navigator.clipboard.writeText(link);
      }

      toastFactory({ type: 'success', title: getSuccessMessage(target, formData) });
      props.onDone?.(invoiceId);
    } catch (error) {
      onError(error as Error);
    } finally {
      setSubmitTarget(undefined);
    }
  };

  return {
    customer,
    isLoading,
    isSaving: isOnBeforeDone || isSaving,
    submitHandler,
    submitTarget,
    isDraft,
  };
};

export const useInvoiceActions = ({ invoiceId, onError }: Pick<Props, 'invoiceId' | 'onError'>) => {
  const { data: invoice, ...invoiceModel } = useInvoice({ id: invoiceId, onError });
  const { data: customer, ...customerModel } = useCustomer({ id: invoice?.customerId, onError });

  const issueInvoice = async (formData: CreateInvoiceFormFields) => {
    if (!customer?.email && formData.email) {
      await customerModel.update({ email: formData.email });
    }
    return invoiceModel.issue();
  };

  // Make sure we never change the value of isDraft when invoice is updated
  const isDraft = useMemo(() => invoice && !invoice.issuedAt, [invoice?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    issueInvoice,
    customer,
    isDraft,
    isLoading: invoiceModel.isLoading || customerModel.isLoading,
    isSaving: invoiceModel.isMutating || customerModel.isMutating,
    paymentRequestLink: invoice?.paymentRequest?.link,
    shareInvoice: invoiceModel.share,
  };
};
